import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaSearch, FaReceipt, FaCheckCircle, FaClock, FaTimesCircle, FaDollarSign, FaFilePdf, FaTimes, FaUser, FaPhone, FaCalendarAlt, FaMoneyBillWave, FaBoxOpen, FaDownload } from 'react-icons/fa';
import { saleService } from '../../services/saleService';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';

const SalesManagement = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSales, setFilteredSales] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSales();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = sales.filter(sale =>
        sale.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sale.items?.some(item => item.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredSales(filtered);
    } else {
      setFilteredSales(sales);
    }
  }, [searchTerm, sales]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const response = await saleService.getSales();
      setSales(response.data);
      setFilteredSales(response.data);
    } catch (error) {
      toast.error('Failed to fetch sales');
    } finally {
      setLoading(false);
    }
  };

  const handleViewSale = async (saleId) => {
    setDetailLoading(true);
    try {
      const response = await saleService.getSaleById(saleId);
      setSelectedSale(response.data);
    } catch (error) {
      toast.error('Failed to fetch sale details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeSaleModal = () => {
    setSelectedSale(null);
  };

  const generateInvoicePDF = useCallback((sale) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let y = 20;

    // --- Header ---
    doc.setFillColor(30, 64, 175); // primary-800
    doc.rect(0, 0, pageWidth, 45, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', margin, y + 8);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`#${sale.invoiceNumber || 'N/A'}`, margin, y + 16);

    doc.setFontSize(10);
    doc.text(`Date: ${new Date(sale.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth - margin, y + 8, { align: 'right' });
    doc.text(`Status: ${(sale.status || 'completed').toUpperCase()}`, pageWidth - margin, y + 16, { align: 'right' });

    y = 55;

    // --- Shop & Customer Info ---
    doc.setTextColor(30, 64, 175);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Waziri Mobile', margin, y);
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Mobile Shop Management', margin, y + 5);

    doc.setTextColor(30, 64, 175);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', pageWidth / 2 + 10, y);
    doc.setTextColor(50, 50, 50);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(sale.customer?.name || 'Walk-in Customer', pageWidth / 2 + 10, y + 6);
    if (sale.customer?.phone) {
      doc.text(`Phone: ${sale.customer.phone}`, pageWidth / 2 + 10, y + 12);
    }
    if (sale.customer?.email) {
      doc.text(`Email: ${sale.customer.email}`, pageWidth / 2 + 10, y + 18);
    }
    if (sale.customer?.address) {
      doc.text(`Address: ${sale.customer.address}`, pageWidth / 2 + 10, y + 24);
    }

    y += 35;

    // --- Divider ---
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    // --- Sold By ---
    if (sale.soldBy?.name) {
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(9);
      doc.text(`Sold By: ${sale.soldBy.name}`, margin, y);
      y += 8;
    }

    // --- Items Table Header ---
    doc.setFillColor(243, 244, 246);
    doc.rect(margin, y, pageWidth - margin * 2, 10, 'F');
    doc.setTextColor(60, 60, 60);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('#', margin + 3, y + 7);
    doc.text('Product', margin + 12, y + 7);
    doc.text('Qty', pageWidth - margin - 70, y + 7, { align: 'right' });
    doc.text('Price', pageWidth - margin - 40, y + 7, { align: 'right' });
    doc.text('Total', pageWidth - margin - 3, y + 7, { align: 'right' });
    y += 14;

    // --- Items Rows ---
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    (sale.items || []).forEach((item, index) => {
      const productName = item.product?.name || `Product ${index + 1}`;
      const quantity = item.quantity || 1;
      const price = item.salePrice || 0;
      const total = price * quantity;

      // Zebra striping
      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 252);
        doc.rect(margin, y - 4, pageWidth - margin * 2, 10, 'F');
      }

      doc.setFontSize(9);
      doc.text(String(index + 1), margin + 3, y + 3);
      // Truncate long product names
      const truncatedName = productName.length > 40 ? productName.substring(0, 37) + '...' : productName;
      doc.text(truncatedName, margin + 12, y + 3);
      doc.text(String(quantity), pageWidth - margin - 70, y + 3, { align: 'right' });
      doc.text(`RS ${price.toLocaleString()}`, pageWidth - margin - 40, y + 3, { align: 'right' });
      doc.text(`RS ${total.toLocaleString()}`, pageWidth - margin - 3, y + 3, { align: 'right' });
      y += 10;
    });

    y += 5;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 8;

    // --- Totals ---
    const totalsX = pageWidth - margin - 3;
    const labelsX = pageWidth - margin - 65;

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');

    doc.text('Subtotal:', labelsX, y, { align: 'right' });
    doc.text(`RS ${(sale.subtotal || 0).toLocaleString()}`, totalsX, y, { align: 'right' });
    y += 7;

    if (sale.discount > 0) {
      doc.setTextColor(220, 38, 38);
      doc.text('Discount:', labelsX, y, { align: 'right' });
      doc.text(`- RS ${sale.discount.toLocaleString()}`, totalsX, y, { align: 'right' });
      y += 7;
    }

    if (sale.tax > 0) {
      doc.setTextColor(100, 100, 100);
      doc.text('Tax:', labelsX, y, { align: 'right' });
      doc.text(`RS ${sale.tax.toLocaleString()}`, totalsX, y, { align: 'right' });
      y += 7;
    }

    y += 2;
    doc.setDrawColor(30, 64, 175);
    doc.setLineWidth(1);
    doc.line(labelsX - 20, y, totalsX, y);
    y += 8;

    doc.setFillColor(30, 64, 175);
    doc.roundedRect(labelsX - 25, y - 6, totalsX - labelsX + 28, 14, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total:', labelsX, y + 3, { align: 'right' });
    doc.text(`RS ${(sale.total || 0).toLocaleString()}`, totalsX, y + 3, { align: 'right' });
    y += 16;

    // --- Payment Info ---
    doc.setTextColor(80, 80, 80);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Payment Method: ${(sale.paymentMethod || 'cash').replace(/_/g, ' ').toUpperCase()}`, margin, y);
    y += 6;
    doc.text(`Paid Amount: RS ${(sale.paidAmount || 0).toLocaleString()}`, margin, y);
    if (sale.dueAmount > 0) {
      y += 6;
      doc.setTextColor(220, 38, 38);
      doc.text(`Due Amount: RS ${sale.dueAmount.toLocaleString()}`, margin, y);
    }
    y += 10;

    // --- Warranty ---
    if (sale.warranty?.provided) {
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Warranty Information', margin, y);
      y += 6;
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      if (sale.warranty.duration) {
        doc.text(`Duration: ${sale.warranty.duration}`, margin, y);
        y += 5;
      }
      if (sale.warranty.expiryDate) {
        doc.text(`Expiry: ${new Date(sale.warranty.expiryDate).toLocaleDateString()}`, margin, y);
        y += 5;
      }
      y += 5;
    }

    // --- Notes ---
    if (sale.notes) {
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes', margin, y);
      y += 6;
      doc.setTextColor(80, 80, 80);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(sale.notes, pageWidth - margin * 2);
      doc.text(lines, margin, y);
      y += lines.length * 5 + 5;
    }

    // --- Footer ---
    const footerY = doc.internal.pageSize.getHeight() - 20;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text('Thank you for your purchase! For any queries, please contact us.', pageWidth / 2, footerY, { align: 'center' });
    doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, footerY + 5, { align: 'center' });

    // Save
    doc.save(`Invoice_${sale.invoiceNumber || sale._id}.pdf`);
    toast.success('Invoice PDF downloaded!');
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading sales...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Sales Management</h1>
            <p className="text-primary-100">Track and manage all sales transactions</p>
          </div>
          <button
            onClick={() => navigate('/admin/sales/new')}
            className="bg-white text-primary-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center gap-2 shadow-lg"
          >
            <FaPlus /> New Sale
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-4">
        <div className="relative">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search sales by invoice, customer, or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-900"
          />
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <FaReceipt className="text-2xl opacity-80" />
            <div>
              <p className="text-sm opacity-90">Total Sales</p>
              <p className="text-2xl font-bold">{sales.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <FaDollarSign className="text-2xl opacity-80" />
            <div>
              <p className="text-sm opacity-90">Total Revenue</p>
              <p className="text-lg font-bold">RS:{sales.reduce((acc, s) => acc + (s.total || 0), 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-4 text-white">
          <div className="flex items-center gap-3">
            <FaCheckCircle className="text-2xl opacity-80" />
            <div>
              <p className="text-sm opacity-90">Completed</p>
              <p className="text-2xl font-bold">{sales.filter(s => s.status === 'completed').length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Invoice #</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Products</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Date</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredSales.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="text-6xl mb-4">🧾</div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800 dark:text-white">No sales found</h3>
                    <p className="text-gray-500 dark:text-gray-400">
                      {searchTerm ? 'Try a different search term' : 'No sales recorded yet'}
                    </p>
                  </td>
                </tr>
              ) : (
                <AnimatePresence>
                  {filteredSales.map((sale, index) => (
                    <motion.tr
                      key={sale._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">#{sale.invoiceNumber}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900 dark:text-white">{sale.customer?.name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {sale.items?.length > 0
                            ? sale.items.map(item => item.product?.name || 'Product').join(', ')
                            : 'N/A'}
                        </div>
                        {sale.items?.length > 1 && (
                          <span className="text-xs text-gray-500">({sale.items.length} items)</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-gray-900 dark:text-white">RS {(sale.total || 0).toLocaleString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(sale.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                          sale.status === 'completed'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : sale.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {sale.status === 'completed' && <FaCheckCircle size={10} />}
                          {sale.status === 'pending' && <FaClock size={10} />}
                          {sale.status === 'cancelled' && <FaTimesCircle size={10} />}
                          {sale.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewSale(sale._id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                            title="View sale details"
                          >
                            <FaEye />
                          </button>
                          <button
                            onClick={() => generateInvoicePDF(sale)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Download Invoice PDF"
                          >
                            <FaFilePdf />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sale Detail Modal */}
      <AnimatePresence>
        {(selectedSale || detailLoading) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={closeSaleModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {detailLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
              ) : selectedSale ? (
                <>
                  {/* Modal Header */}
                  <div className="bg-gradient-to-r from-primary-600 to-primary-800 p-6 rounded-t-2xl text-white flex justify-between items-start">
                    <div>
                      <h2 className="text-xl font-bold">Sale Details</h2>
                      <p className="text-primary-200 text-sm mt-1 font-mono">#{selectedSale.invoiceNumber}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => generateInvoicePDF(selectedSale)}
                        className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                        title="Download Invoice PDF"
                      >
                        <FaDownload />
                      </button>
                      <button
                        onClick={closeSaleModal}
                        className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 space-y-6">
                    {/* Customer & Payment Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                        <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold mb-3">
                          <FaUser /> Customer
                        </div>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedSale.customer?.name || 'Walk-in Customer'}</p>
                        {selectedSale.customer?.phone && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1 mt-1">
                            <FaPhone size={10} /> {selectedSale.customer.phone}
                          </p>
                        )}
                        {selectedSale.customer?.email && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedSale.customer.email}</p>
                        )}
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                        <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold mb-3">
                          <FaMoneyBillWave /> Payment
                        </div>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">{(selectedSale.paymentMethod || 'cash').replace(/_/g, ' ')}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
                          <FaCalendarAlt size={10} /> {new Date(selectedSale.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                        <span className={`inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          selectedSale.status === 'completed'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : selectedSale.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {selectedSale.status === 'completed' && <FaCheckCircle size={10} />}
                          {selectedSale.status === 'pending' && <FaClock size={10} />}
                          {selectedSale.status}
                        </span>
                      </div>
                    </div>

                    {/* Items */}
                    <div>
                      <div className="flex items-center gap-2 text-primary-600 dark:text-primary-400 font-semibold mb-3">
                        <FaBoxOpen /> Items
                      </div>
                      <div className="border dark:border-gray-700 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">#</th>
                              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">Product</th>
                              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Qty</th>
                              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Price</th>
                              <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-600 dark:text-gray-300">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {(selectedSale.items || []).map((item, idx) => (
                              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{idx + 1}</td>
                                <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{item.product?.name || 'Product'}</td>
                                <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{item.quantity}</td>
                                <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">RS {(item.salePrice || 0).toLocaleString()}</td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-white">RS {((item.salePrice || 0) * (item.quantity || 1)).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Summary */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-2">
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                        <span>Subtotal</span>
                        <span>RS {(selectedSale.subtotal || 0).toLocaleString()}</span>
                      </div>
                      {selectedSale.discount > 0 && (
                        <div className="flex justify-between text-sm text-red-500">
                          <span>Discount</span>
                          <span>- RS {selectedSale.discount.toLocaleString()}</span>
                        </div>
                      )}
                      {selectedSale.tax > 0 && (
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                          <span>Tax</span>
                          <span>RS {selectedSale.tax.toLocaleString()}</span>
                        </div>
                      )}
                      <div className="border-t dark:border-gray-600 pt-2 mt-2 flex justify-between font-bold text-lg text-gray-900 dark:text-white">
                        <span>Total</span>
                        <span>RS {(selectedSale.total || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 pt-1">
                        <span>Paid Amount</span>
                        <span className="text-green-600 font-medium">RS {(selectedSale.paidAmount || 0).toLocaleString()}</span>
                      </div>
                      {selectedSale.dueAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-red-500">Due Amount</span>
                          <span className="text-red-500 font-medium">RS {selectedSale.dueAmount.toLocaleString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Warranty */}
                    {selectedSale.warranty?.provided && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                        <p className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-1">Warranty</p>
                        {selectedSale.warranty.duration && (
                          <p className="text-sm text-blue-600 dark:text-blue-300">Duration: {selectedSale.warranty.duration}</p>
                        )}
                        {selectedSale.warranty.expiryDate && (
                          <p className="text-sm text-blue-600 dark:text-blue-300">Expires: {new Date(selectedSale.warranty.expiryDate).toLocaleDateString()}</p>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {selectedSale.notes && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                        <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-1">Notes</p>
                        <p className="text-sm text-yellow-600 dark:text-yellow-300">{selectedSale.notes}</p>
                      </div>
                    )}

                    {/* Sold By */}
                    {selectedSale.soldBy?.name && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 text-right">Sold by: {selectedSale.soldBy.name}</p>
                    )}
                  </div>
                </>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SalesManagement;
